/**
 * OnboardingEnforcementGate — SELF-PACED setup guidance (owner directive, 2026-06).
 *
 * The hard lock is REMOVED. The Provider OS is fully usable from day one.
 * The 8-step Business Profile setup flow, dashboard banner, Ari briefing item,
 * and progress tracking all remain — the provider completes them at their own
 * pace. Features that genuinely require setup (payments, public visibility,
 * RemodConnect eligibility) keep their natural in-context gates.
 *
 * This component now only delivers a ONE-TIME gentle Ari heads-up when a new
 * provider owner/admin enters the OS with an incomplete profile:
 *   "You're in. I'll help you finish your business setup as you go. Your
 *    profile is currently {completed} of 8 steps complete."
 * Shown once per company (localStorage), never blocking, never redirecting.
 */
import { useEffect } from 'react';
import { toast } from 'sonner';
import { useBusinessSetup } from '@/contexts/BusinessSetupContext';

const OWNER_ROLES = ['owner', 'admin', 'business', 'business_owner', 'provider'];

const readRoles = () => {
  try {
    const arr = JSON.parse(localStorage.getItem('user_roles') || '[]');
    const single = localStorage.getItem('user_role');
    return [...(Array.isArray(arr) ? arr : []), single]
      .filter(Boolean)
      .map((r) => String(r).toLowerCase());
  } catch {
    return [];
  }
};

const OnboardingEnforcementGate = ({ children }) => {
  const { setup } = useBusinessSetup();

  useEffect(() => {
    if (!setup || !setup.enforced || setup.setup_complete !== false) return;

    const roles = readRoles();
    const isOwnerAdmin = roles.some((r) => OWNER_ROLES.includes(r));
    const isSuperAdmin = roles.includes('super_admin');
    const isToolsUser =
      roles.includes('tools_user') || localStorage.getItem('tools_only') === 'true';
    const isDemo = localStorage.getItem('demo_mode') === 'provider';
    if (!isOwnerAdmin || isSuperAdmin || isToolsUser || isDemo) return;

    const key = `ari_setup_welcome_${setup.company_id || 'default'}`;
    if (localStorage.getItem(key) === '1') return;
    localStorage.setItem(key, '1');

    const setupIds = setup.setup_step_ids || [];
    const completed = (setup.steps || []).filter(
      (s) => setupIds.includes(s.id) && s.status === 'complete'
    ).length;

    toast('Ari', {
      description: `You're in. I'll help you finish your business setup as you go. Your profile is currently ${completed} of 8 steps complete.`,
      duration: 9000,
      id: 'ari-setup-welcome',
    });
  }, [setup]);

  return children;
};

export default OnboardingEnforcementGate;
